import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GraduationCap, AlertCircle, CheckCircle2 } from 'lucide-react';
import { TRAINING_ROLE_OPTIONS } from '@/lib/trainingRoles';
import {
  labelForRequiredField,
  requiredFieldType,
  REQUIRED_FIELD_DROPDOWN_OPTIONS,
} from '@/lib/requiredFieldOptions';
import { subjects, studies } from '@/api/client';
import { SingleSelectSearch, type SingleSelectOption } from '@/components/ui/SingleSelectSearch';
import { subjectDisplayId } from '@/lib/subjectLabel';

/**
 * Parse the catalog's required_fields_json — [] on empty/malformed input.
 * Keys are normalised to the lowercase vocabulary (the seeded catalog
 * spells them "Study" / "Product" / "Person") so labels, input types and
 * the context-bound `study` filter all resolve; the server matches
 * case-blind too.
 */
export function parseRequiredFields(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const keys = parsed
      .filter((v): v is string => typeof v === 'string')
      .map((v) => v.trim().toLowerCase())
      .filter((v) => v.length > 0);
    return [...new Set(keys)];
  } catch {
    return [];
  }
}

// `form-input` is not a real class in this project (no @tailwindcss/forms
// plugin registered) — it silently applied zero styling, leaving these
// inputs at bare browser defaults. Built explicitly here instead so size,
// radius, and focus state are all under our control.
const FIELD_BASE =
  'h-9 w-full rounded-md border bg-white px-3 text-xs text-ink-primary shadow-sm ' +
  'transition-colors placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-offset-0';
const FIELD_NEUTRAL = 'border-surface-border focus:border-accent-500 focus:ring-accent-500/20';
const FIELD_REQUIRED_EMPTY =
  'border-amber-400 placeholder:text-amber-500/80 focus:border-amber-500 focus:ring-amber-400/30';

/**
 * Training module Phase 3 — the doc type's catalog-declared required
 * fields (client: "for a document type, if we select training and
 * language... when they are uploading to that type, they have to
 * select the data"), rendered as plain text inputs. Values are
 * enforced client-side here (blocks the drop) AND server-side in
 * finaliseVersion.ts — this is UX, not the source of truth for the
 * requirement.
 */
export function RequiredMetadataFields({
  fieldKeys,
  values,
  onChange,
  studyId,
}: {
  /** Keys the uploader must fill. Context-bound keys such as `study` are
   *  filtered out by the caller — they are never rendered here. */
  fieldKeys: readonly string[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** Scopes the `participant` / `person` pickers to this study's real
   *  subjects / delegates. Falls back to a plain text box when absent. */
  studyId?: string;
}) {
  const remaining = fieldKeys.filter((key) => !values[key]?.trim()).length;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3.5 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 font-semibold text-amber-900">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Required for this document type
          </div>
          <p className="mt-0.5 text-[11px] text-amber-800">
            Upload is blocked until every field below is filled in.
          </p>
        </div>
        {fieldKeys.length > 0 && (
          <span
            className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              remaining === 0
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-200 text-amber-900'
            }`}
          >
            {remaining === 0 ? 'All set' : `${remaining} remaining`}
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {fieldKeys.map((key) => {
          const filled = !!values[key]?.trim();
          return (
            <label key={key} className="block">
              <span className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                {labelForRequiredField(key)}
                {filled ? (
                  <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                ) : (
                  <span className="text-danger-600">*</span>
                )}
              </span>
              <EditableRequiredField
                fieldKey={key}
                value={values[key] ?? ''}
                onChange={(v) => onChange(key, v)}
                filled={filled}
                studyId={studyId}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One catalog required field, rendered per its declared input type
 * (`requiredFieldType`) — a plain text box for the free-form fields,
 * a date picker, a fixed dropdown for bounded vocabularies, or a real
 * searchable picker of the study's own subjects/delegates for
 * `participant` / `person`.
 */
function EditableRequiredField({
  fieldKey,
  value,
  onChange,
  filled,
  studyId,
}: {
  fieldKey: string;
  value: string;
  onChange: (value: string) => void;
  filled: boolean;
  studyId?: string;
}) {
  const inputClass = `${FIELD_BASE} ${filled ? FIELD_NEUTRAL : FIELD_REQUIRED_EMPTY}`;
  const type = requiredFieldType(fieldKey);

  if (type === 'date') {
    return <input type="date" className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />;
  }

  if (type === 'dropdown') {
    const options = REQUIRED_FIELD_DROPDOWN_OPTIONS[fieldKey] ?? [];
    return (
      <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }

  if (type === 'participant') {
    return <ParticipantPicker value={value} onChange={onChange} studyId={studyId} filled={filled} />;
  }

  if (type === 'person') {
    return <PersonPicker value={value} onChange={onChange} studyId={studyId} filled={filled} />;
  }

  return (
    <input
      type="text"
      className={inputClass}
      placeholder="Required"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** `participant` required field — real subjects on this study, labelled
 *  by their Subject ID, else REF-SUB-xxxx (never PHI). Degrades to a plain text box when
 *  no study context is available. */
function ParticipantPicker({
  value,
  onChange,
  studyId,
  filled,
}: {
  value: string;
  onChange: (value: string) => void;
  studyId?: string;
  filled: boolean;
}) {
  const q = useQuery({
    queryKey: ['subjects', 'for-required-field', studyId],
    queryFn: () => subjects.list({ study_id: studyId, limit: 500 }),
    enabled: !!studyId,
    staleTime: 60_000,
  });
  const options: SingleSelectOption[] = useMemo(
    () => (q.data?.rows ?? []).map((s) => ({ value: s.subject_id, label: subjectDisplayId(s) })),
    [q.data],
  );
  if (!studyId) {
    return (
      <input
        type="text"
        className={`${FIELD_BASE} ${filled ? FIELD_NEUTRAL : FIELD_REQUIRED_EMPTY}`}
        placeholder="Participant"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  const selected = options.find((o) => o.label === value);
  return (
    <SingleSelectSearch
      options={options}
      value={selected?.value ?? ''}
      onChange={(v) => onChange(options.find((o) => o.value === v)?.label ?? '')}
      placeholder={q.isLoading ? 'Loading participants…' : 'Select participant'}
      triggerClassName={filled ? FIELD_NEUTRAL : FIELD_REQUIRED_EMPTY}
    />
  );
}

/** `person` required field — real staff/delegates on this study, labelled
 *  by full name. Degrades to a plain text box when no study context is
 *  available. */
function PersonPicker({
  value,
  onChange,
  studyId,
  filled,
}: {
  value: string;
  onChange: (value: string) => void;
  studyId?: string;
  filled: boolean;
}) {
  const q = useQuery({
    queryKey: ['studies', studyId, 'delegated-users', 'for-required-field'],
    queryFn: () => studies.listDelegatedUsers(studyId!),
    enabled: !!studyId,
    staleTime: 60_000,
  });
  const options: SingleSelectOption[] = useMemo(
    () => (q.data?.users ?? []).map((u) => ({ value: u.user_id, label: u.full_name })),
    [q.data],
  );
  if (!studyId) {
    return (
      <input
        type="text"
        className={`${FIELD_BASE} ${filled ? FIELD_NEUTRAL : FIELD_REQUIRED_EMPTY}`}
        placeholder="Person"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  const selected = options.find((o) => o.label === value);
  return (
    <SingleSelectSearch
      options={options}
      value={selected?.value ?? ''}
      onChange={(v) => onChange(options.find((o) => o.value === v)?.label ?? '')}
      placeholder={q.isLoading ? 'Loading people…' : 'Select person'}
      triggerClassName={filled ? FIELD_NEUTRAL : FIELD_REQUIRED_EMPTY}
    />
  );
}

/**
 * Training module Phase 3 — optional effective / expiry dates (client:
 * "the date is important... what date this document is, and if the
 * document can expire, what date it is expiring"). Both optional.
 */
export function EffectiveExpiryDateFields({
  effectiveAt,
  onEffectiveAtChange,
  expiresAt,
  onExpiresAtChange,
}: {
  effectiveAt: string;
  onEffectiveAtChange: (v: string) => void;
  expiresAt: string;
  onExpiresAtChange: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 text-xs">
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium text-ink-secondary">
          Effective date <span className="font-normal text-ink-muted">(optional)</span>
        </span>
        <input
          type="date"
          className={`${FIELD_BASE} ${FIELD_NEUTRAL}`}
          value={effectiveAt}
          onChange={(e) => onEffectiveAtChange(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium text-ink-secondary">
          Expiry date <span className="font-normal text-ink-muted">(optional)</span>
        </span>
        <input
          type="date"
          className={`${FIELD_BASE} ${FIELD_NEUTRAL}`}
          value={expiresAt}
          onChange={(e) => onExpiresAtChange(e.target.value)}
        />
      </label>
    </div>
  );
}

/**
 * Training module Phase 2 — per-upload "training required?" toggle +
 * study-role picker, shown above the drop zone. Overrides the doc
 * type's catalog default (set in eISF Master Data) for this one
 * upload only — it never writes back to the catalog row.
 */
export function TrainingRequiredToggle({
  checked,
  onCheckedChange,
  roles,
  onRolesChange,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  roles: string[];
  onRolesChange: (next: string[]) => void;
}) {
  function toggleRole(value: string) {
    onRolesChange(roles.includes(value) ? roles.filter((r) => r !== value) : [...roles, value]);
  }

  return (
    <div className="rounded-lg border border-surface-border bg-surface-muted/30 p-3.5 text-xs">
      <label className="flex cursor-pointer items-center gap-2 font-medium text-ink-primary">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
        />
        <GraduationCap className="h-3.5 w-3.5 text-ink-muted" />
        Training required for this upload
      </label>
      {checked && (
        <div className="mt-2 pl-6">
          <div className="mb-1 text-[11px] text-ink-muted">
            Required for which study roles — leave empty to assign every delegate on the study.
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {TRAINING_ROLE_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-1.5 text-[11px]">
                <input
                  type="checkbox"
                  checked={roles.includes(opt.value)}
                  onChange={() => toggleRole(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
